import { useState, useEffect, useCallback, useRef } from "react";
import { Form } from "antd";
import { useCheckSlugAvailability } from "#src/hooks";
import { slugify } from "utils/text";

export interface UseSlugCheckerOptions {
  checkEndpoint: (slug: string) => string;
  initialSlug?: string; // INITIAL SLUG (FROM BACKEND / SAVED TO PROJECT)
  initialValue?: string; // INITIAL VALUE
  onSlugCheck?: (slug: string, isAvailable: boolean) => void;
  generateSlug?: () => string;
}

export interface UseSlugCheckerReturn {
  form: ReturnType<typeof Form.useForm>[0];
  slug: string;
  availabilityResult: {
    checked: boolean;
    available: boolean;
    alternate_slug?: string;
  };
  isLoading: boolean;
  isSlugAvailable: boolean;
  isSlugUnavailable: boolean;
  hasChecked: boolean;
  handleCheck: (
    slugOverride?: string
  ) => Promise<{ isAvailable: boolean; slug: string }>;
  handleSlugChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleKeyPress: (e: React.KeyboardEvent) => void;
  handleSelectAlternateSlug: () => void;
  handleGenerateSlug: () => void;
}

type AvailabilityResult = {
  checked: boolean;
  available: boolean;
  alternate_slug?: string;
};

export const useSlugChecker = (
  options: UseSlugCheckerOptions
): UseSlugCheckerReturn => {
  const {
    checkEndpoint,
    initialSlug,
    initialValue,
    onSlugCheck,
    generateSlug,
  } = options;

  const [form] = Form.useForm();
  const [availabilityResult, setAvailabilityResult] =
    useState<AvailabilityResult>({ checked: false, available: false });

  // TRACK LAST CHECKED SLUG TO PREVENT DUPLICATE API CALLS
  const lastCheckedSlugRef = useRef<string | null>(null);

  const { checkSlug, isLoading } = useCheckSlugAvailability(checkEndpoint);
  const slug = Form.useWatch("slug", form) || "";

  const handleCheck = useCallback(
    async (slugOverride: string = slug) => {
      // SKIP IF THIS SLUG HAS BEEN CHECKED
      if (lastCheckedSlugRef.current === slugOverride) {
        return {
          isAvailable: availabilityResult.available,
          slug: slugOverride,
        };
      }

      // IF MATCHES INITIAL SLUG, IT WILL BE AVAILABLE
      if (initialSlug && slugOverride === initialSlug) {
        lastCheckedSlugRef.current = slugOverride;
        setAvailabilityResult({ checked: true, available: true });
        onSlugCheck?.(slugOverride, true);
        return { isAvailable: true, slug: slugOverride };
      }

      try {
        const result = await checkSlug(slugOverride);
        const isAvailable = result?.is_input_available ?? false;
        lastCheckedSlugRef.current = slugOverride;
        setAvailabilityResult({
          checked: true,
          available: isAvailable,
          alternate_slug: result?.alternate_slug,
        });
        onSlugCheck?.(slugOverride, isAvailable);
        return { isAvailable, slug: slugOverride };
      } catch (error) {
        console.error("Error checking slug availability:", error);
        lastCheckedSlugRef.current = slugOverride;
        setAvailabilityResult({ checked: true, available: false });
        onSlugCheck?.(slugOverride, false);
        return { isAvailable: false, slug: slugOverride };
      }
    },
    [slug, initialSlug, checkSlug, onSlugCheck, availabilityResult]
  );

  const handleSelectAlternateSlug = useCallback(() => {
    if (availabilityResult.alternate_slug) {
      const alternateSlug = availabilityResult.alternate_slug;
      form.setFieldValue("slug", alternateSlug);
      lastCheckedSlugRef.current = alternateSlug;
      setAvailabilityResult({ checked: true, available: true });
      onSlugCheck?.(alternateSlug, true);
    }
  }, [availabilityResult.alternate_slug, form, onSlugCheck]);

  const handleSlugChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const slugifiedValue = slugify(e.target.value, true);
      form.setFieldValue("slug", slugifiedValue);

      // If matches initial slug, mark as available immediately
      if (initialSlug && slugifiedValue === initialSlug) {
        lastCheckedSlugRef.current = slugifiedValue;
        setAvailabilityResult({ checked: true, available: true });
      } else {
        // Reset checked state for new slugs
        lastCheckedSlugRef.current = null;
        setAvailabilityResult({ checked: false, available: false });
      }
    },
    [form, initialSlug]
  );

  const handleKeyPress = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        void handleCheck();
      }
    },
    [handleCheck]
  );

  const handleGenerateSlug = useCallback(() => {
    if (generateSlug) {
      const newSlug = generateSlug();
      form.setFieldValue("slug", newSlug);
      void handleCheck(newSlug);
    }
  }, [generateSlug, form, handleCheck]);

  // Sync initialValue with form on mount or when it changes
  useEffect(() => {
    if (initialValue && initialValue !== form.getFieldValue("slug")) {
      form.setFieldValue("slug", initialValue);
    }
  }, [initialValue, form]);

  const isSlugAvailable =
    availabilityResult.checked && availabilityResult.available;
  const isSlugUnavailable =
    availabilityResult.checked && !availabilityResult.available;
  const hasChecked = availabilityResult.checked;

  return {
    form,
    slug,
    availabilityResult,
    isLoading,
    isSlugAvailable,
    isSlugUnavailable,
    hasChecked,
    handleCheck,
    handleSlugChange,
    handleKeyPress,
    handleSelectAlternateSlug,
    handleGenerateSlug,
  };
};
